/**
 * The Settings hub entries this person passes the gate of (M286 idea 11), in list order. The hub renders them, the
 * sidebar shows its Settings item only when there is one, and settings search indexes them. A role that loses
 * `settings_submarkets` in the matrix stops seeing that entry at once. Each route checks the same rule itself.
 */
import { SETTINGS_REGISTRY } from '@aiga/app-config/settings';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useCanEditListingTemplates } from '../listings/useListingsAccess';
import { useCanWorkPropertyManagement } from '../property-management/usePropertyManagementAccess';
import { isAppSettingsGroupVisible, useCanSeeAppSettings } from '../settings-registry/useCanSeeAppSettings';
import { useCanManageForms } from '../forms/useFormsAccess';
import { useCanOpenDataMaintenance } from './useDataMaintenanceAccess';
import { useDataCurationTabs } from './useDataCurationAccess';
import { APP_SETTINGS_PATH, SETTINGS_ENTRIES, type SettingsEntry, type SettingsEntryGate } from './settingsEntries';

export function useVisibleSettingsEntries(): SettingsEntry[] {
  // The AI entries work for AIG accounts only; everyone else does not see them (Noah, 2026-10-08; hidden, not greyed, M286).
  const isAig = useIsSuperAdmin();
  const canSeeAi = useFeatureAccess('ai_lab');

  const passes: Record<SettingsEntryGate, boolean> = {
    settings_permissions: useFeatureAccess('settings_permissions'),
    manage_users: useFeatureAccess('manage_users'),
    settings_data_sources: useFeatureAccess('settings_data_sources'),
    settings_submarkets: useFeatureAccess('settings_submarkets'),
    listings_templates: useCanEditListingTemplates(),
    property_management_work: useCanWorkPropertyManagement(),
    ai_lab: canSeeAi && isAig,
    // The county operations (feature:settings_data_maintenance) or the Land area mismatch list (Research / admins).
    settings_data_maintenance: useCanOpenDataMaintenance(),
    app_settings: useCanSeeAppSettings(),
    manage_forms: useCanManageForms().canManage,
    data_curation: useDataCurationTabs().length > 0,
  };

  // App settings lists the registry groups the page shows this person.
  const appSettingsGroups = SETTINGS_REGISTRY.groups.filter((g) => isAppSettingsGroupVisible(g.id, isAig)).map((g) => g.label);

  return SETTINGS_ENTRIES.filter((e) => passes[e.gate]).map((e) =>
    e.path === APP_SETTINGS_PATH ? { ...e, subOptions: [appSettingsGroups.join(' · '), ...e.subOptions] } : e,
  );
}

/**
 * Who opens the hub itself: feature:settings, or anyone who may open Data Maintenance (M286 idea 15). Research has no
 * feature:settings by default, and the Land area mismatch list there is theirs to work: the hub shows them the entries
 * above (Data Maintenance, and any other they pass). App.tsx SettingsHubRoute and the sidebar's Settings item use it.
 */
export function useCanOpenSettingsHub(): boolean {
  const canSeeHub = useFeatureAccess('settings');
  const canOpenDataMaintenance = useCanOpenDataMaintenance();
  return canSeeHub || canOpenDataMaintenance;
}
